import { useQuery, useQueryClient } from "@tanstack/react-query";
import useMainFilter from "./useMainFilter";
import { compact, map, uniq, sortBy, find } from "lodash-es";
import { CACHE_KEYS, ETransactionType } from "../helpers/constants";
import { searchTransactions } from "../api/transactions";
import {
  filterTransactions,
  getCurrencyDataByCode,
  getTodayTransactionsFromTransactions,
  getUniqueYearsFromTransactions,
} from "../helpers/utils";
import moment from "moment";
import useCurrentUser from "./useCurrentUser";
import { TRANSACTION_STATUS_PENDING } from "../constants/transaction-status";
import { useMemo } from "react";
import { paymentMethod, transactionStatus } from "../helpers/transactions";

const REFETCH_MINUTES = 10;
const REFETCH_INTERVAL = REFETCH_MINUTES * 1000 * 60;

export default function useCurrentAccount() {
  const queryClient = useQueryClient();
  const { getAccountById } = useCurrentUser();
  const { mainFilter } = useMainFilter();

  const queryKey = [
    CACHE_KEYS.ALL_CURRENT_ACCOUNT_TRANSACTIONS,
    mainFilter?.account,
  ].join("-");

  const {
    data: allTransactions = [],
    isLoading = true,
    isRefetching,
  } = useQuery({
    queryKey: [queryKey],
    queryFn: async () => {
      const response = await searchTransactions({
        fieldsToSearch: { account: mainFilter?.account },
      });

      return response?.data || [];
    },
    refetchInterval: REFETCH_INTERVAL,
    enabled: !!mainFilter.account,
  });

  const accountCategories = useMemo(
    () => uniq(compact(map(allTransactions || [], "category"))),
    [allTransactions]
  );

  const accountDescriptions = useMemo(
    () => uniq(compact(map(allTransactions, "description"))).sort(),
    [allTransactions]
  );

  const accountClientProviders = useMemo(
    () => uniq(compact(map(allTransactions, "payTo"))),
    [allTransactions]
  );

  const accountPaymentMethods = useMemo(
    () => uniq(compact(map(allTransactions, "paymentMethod"))),
    [allTransactions]
  );

  const accountStatuses = useMemo(
    () => uniq(compact(map(allTransactions, "status"))),
    [allTransactions]
  );

  const activeTransactions = useMemo(() => {
    return filterTransactions(allTransactions, {
      archived: false,
      shared: false,
      deleted: false,
    });
  }, [allTransactions]);

  const archivedTransactions = useMemo(() => {
    return filterTransactions(allTransactions, {
      archived: true,
      shared: false,
      deleted: false,
    });
  }, [allTransactions]);

  const deletedTransactions = useMemo(() => {
    return filterTransactions(allTransactions, { deleted: true });
  }, [allTransactions]);

  const lastSevenDaysTransactions = useMemo(() => {
    const activeList = activeTransactions;
    const lastDates = Array.from(
      new Set(activeList.map(({ date }: { date: any }) => date))
    ).slice(0, 7);

    return activeList.filter(({ date }: { date: any }) =>
      lastDates.includes(moment(date).format("yyyy-MM-DD"))
    );
  }, [activeTransactions]);

  const todayTransactions = useMemo(() => {
    return getTodayTransactionsFromTransactions({
      transactions: activeTransactions,
    });
  }, [activeTransactions]);

  const receivedTransactions = useMemo(() => {
    return filterTransactions(allTransactions, {
      shared: true,
      deleted: false,
    });
  }, [allTransactions]);

  const transactionsPendingToPay = useMemo(() => {
    return filterTransactions(allTransactions, {
      status: TRANSACTION_STATUS_PENDING,
      type: ETransactionType.EXPENSE,
    });
  }, [allTransactions]);

  const transactionsPendingToCollect = useMemo(() => {
    return filterTransactions(allTransactions, {
      status: TRANSACTION_STATUS_PENDING,
      type: ETransactionType.INCOME,
    });
  }, [allTransactions]);

  const availableYears = useMemo(() => {
    return getUniqueYearsFromTransactions({
      transactions: allTransactions as any,
      account: mainFilter?.account,
    });
  }, [allTransactions, mainFilter?.account]);

  const accountAllPendingTransactions = useMemo(() => {
    return filterTransactions(allTransactions, {
      shared: false,
      archived: false,
      deleted: false,
      status: TRANSACTION_STATUS_PENDING,
    });
  }, [allTransactions]);

  const refetchAccountQueries = async () => {
    return await Promise.allSettled([
      queryClient.refetchQueries({ queryKey }),
      queryClient.refetchQueries({ queryKey: [CACHE_KEYS.ALL_CATEGORIES] }),
    ]);
  };

  const categoriesObject = useMemo(
    () =>
      sortBy(
        map(accountCategories, (category) => ({
          value: category,
          label: category,
        })),
        "label"
      ),
    [accountCategories]
  );

  const clientProvidersObject = useMemo(
    () =>
      sortBy(
        map(accountClientProviders, (clientProvider) => ({
          value: clientProvider,
          label: clientProvider,
        })),
        "label"
      ),
    [accountClientProviders]
  );
  const statusesObject = useMemo(
    () =>
      sortBy(
        map(accountStatuses, (status) => ({
          value: status,
          label: transactionStatus.getNameById(status) || "",
        })),
        "label"
      ),
    [accountStatuses]
  );
  // const paymentMethodsObject = useMemo(
  //   () =>
  //     sortBy(
  //       map(accountPaymentMethods, (paymentMeth) => ({
  //         value: paymentMeth,
  //         label: paymentMethod.getNameById(paymentMeth) || "",
  //       })),
  //       "label"
  //     ),

  //   [accountPaymentMethods]
  // );
  const descriptionsObject = useMemo(
    () =>
      sortBy(
        map(accountDescriptions, (description) => ({
          value: description,
          label: description,
        })),
        "label"
      ),
    [accountDescriptions]
  );

  const getTransactionById = ({ transactionId }: { transactionId: string }) => {
    return find(allTransactions || [], { invoice: transactionId });
  };

  return {
    currentAccountId: mainFilter?.account,
    currentAccountData: getAccountById(mainFilter?.account),
    isLoadingCurrentAccount: isLoading,
    allTransactions,
    archivedTransactions,
    deletedTransactions,
    activeTransactions,
    receivedTransactions,
    availableYears,
    lastSevenDaysTransactions,
    pendingToCollectTransactions: transactionsPendingToCollect,
    pendingToPayTransactions: transactionsPendingToPay,
    currencySymbol:
      getCurrencyDataByCode({
        code: getAccountById(mainFilter?.account)?.currency,
      })?.symbol || "",
    currencyCode:
      getCurrencyDataByCode({
        code: getAccountById(mainFilter?.account)?.currency,
      })?.code || "",
    currencyName:
      getCurrencyDataByCode({
        code: getAccountById(mainFilter?.account)?.currency,
      })?.name || "",
    accountAllPendingTransactions,
    todayTransactions,
    refetchAccountQueries,
    isRefetchingAllTransactions: isRefetching,
    accountCategories,
    accountDescriptions,
    accountClientProviders,
    accountPaymentMethods,
    accountStatuses,
    accountCategoriesObject: categoriesObject,
    accountStatusesObject: statusesObject,
    accountClientProvidersObject: clientProvidersObject,
    // accountPaymentMethodsObject: paymentMethodsObject,
    accountDescriptionsObject: descriptionsObject,
    getTransactionById,
  };
}
